import {
  ResponsiveContainer,
  ScatterChart,
  CartesianGrid,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Cell,
} from 'recharts';
import './chart.css';
import { Box, Icon } from '@chakra-ui/react';
import CustomTooltip from './components/customTooltip/CustomTooltip';
import {
  ISSUE_AGE_CHART_KEYS,
  ISSUE_CHART_KEYS,
  PR_AGEING_CHARTS,
} from '../shared/utils';
import { useCallback, useMemo, useState } from 'react';
import MetricSidebar from './components/MetricSidebar';
import moment from 'moment';
import { TriangleDownIcon } from '@chakra-ui/icons';
import { DateRangeSelection } from '../shared/typings';

export interface BubbleChartDataItem {
  color: string;
  value: number;
  timeFrame: string;
  YAXIS: number;
}

export interface BubbleChartKey {
  key: string;
  name: string;
  color: string;
  chartType: string;
}

interface BubbleChartRechartsProps {
  chartref?: any;
  data: BubbleChartDataItem[];
  keys?: BubbleChartKey[];
  chartMetadata?: any;
  showLabel?: boolean;
  selectedTeam: string;
  hideBtn?: boolean;
  slant?: boolean;
  ticketType?: string;
  selected: DateRangeSelection;
  queryKey?: string;
  repos?: string[];
}

//This component is used to show bubble chart in Aging PRs and Issue Cycle Time Spread
export function BubbleChartRecharts({
  chartref,
  data,
  keys,
  chartMetadata,
  showLabel,
  selectedTeam,
  hideBtn,
  slant,
  ticketType,
  selected,
  queryKey,
  repos,
}: BubbleChartRechartsProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>(null);
  const [currentChartMeta, setCurrentChartMeta] = useState<any>(null);

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
  }, [isOpen]);

  const handleModalOpen = useCallback(() => {
    setIsOpen(true);
  }, [isOpen]);

  const total = useMemo(
    () =>
      data?.reduce(
        (a, b) => {
          return {
            value: a.value + b.value,
          };
        },
        { value: 0 }
      ),
    [data]
  );

  const renderCustomizedLabel = (props: {
    cx: number;
    y: number;
    index: number;
  }) => {
    const x = props.cx;
    const y = props.y - 15;
    const count = data?.[props.index].value;

    const formatCount = (
      count: number,
      total: { value: number },
      key: string
    ) => {
      if (key === 'ISSUE_PROGRESSION_RATE') return `${count}%`;

      const percentage =
        total?.value && total.value !== 0
          ? ((count / total.value) * 100)?.toFixed(1)
          : '0.0';

      return `${count} (${percentage}%)`;
    };
    return (
      <text
        x={x}
        y={y}
        fill="#778899"
        textAnchor="middle"
        fontSize={12}
        fontWeight={500}
      >
        {formatCount(count, total, chartMetadata.chartKey)}
      </text>
    );
  };

  return (
    <>
      <Box
        position="relative"
        width="100%"
        paddingRight={5}
        height={showLabel ? '100%' : '240px'}
      >
        <Box position="absolute" right={3} bottom={6}>
          <Icon as={TriangleDownIcon} style={{ transform: 'rotate(270deg)' }} />
        </Box>

        <ResponsiveContainer>
          <ScatterChart data={data} ref={chartref}>
            <CartesianGrid
              vertical={false}
              horizontal={true}
              horizontalPoints={[135, 135, 135]}
            />

            <XAxis
              dataKey={chartMetadata.xAxis}
              type="category"
              //     padding={{ right: 150, left: 100 }}
            />
            <YAxis
              dataKey={chartMetadata.yAxis}
              type="number"
              tick={true}
              hide={true}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickCount={3}
              //     label={{ value: 'Sunday', position: 'insideRight' }}
            />
            <ZAxis
              dataKey={chartMetadata.zAxis}
              name={keys?.[0]?.name ?? 'Count(%)'}
              type="number"
              scale={'auto'}
              range={[1000, 22000]}
            />
            <Tooltip
              cursor={false}
              wrapperStyle={{ outline: 'none' }}
              content={
                <CustomTooltip
                  total={total.value}
                  chartKey={chartMetadata.chartKey}
                  hideBtn={true}
                />
              }
            />

            <Scatter
              onClick={(event: any, payload: any) => {
                handleModalOpen();
                setClickedData({ ...payload, name: event.timeFrame });
                setCurrentChartMeta(chartMetadata);
              }}
              dataKey="name"
              label={renderCustomizedLabel}
            >
              {data?.map((item, index: number) => (
                <Cell
                  key={index}
                  fill={item.color}
                  style={{ outline: 'none', cursor: 'pointer' }}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
      {[
        ...ISSUE_CHART_KEYS,
        ...ISSUE_AGE_CHART_KEYS,
        ...PR_AGEING_CHARTS,
      ].includes(currentChartMeta?.chartKey) &&
        isOpen && (
          <MetricSidebar
            querykey={queryKey}
            openDrawer={isOpen}
            handleDrawerClose={handleModalClose}
            heading={chartMetadata?.chartTitle}
            subHeading={`${moment(selected?.startDate).format(
              'DD MMM'
            )} - ${moment(selected?.endDate).format('DD MMM')}, ${
              clickedData?.name
            } (${data.find((d) => d?.timeFrame === clickedData?.name)?.value})`}
            payload={{
              ...clickedData,

              payload: {
                startDate: selected?.startDate,
                endDate: selected?.endDate,
              },
            }}
            currentChartMeta={currentChartMeta}
            filterBy={{
              teamId: selectedTeam,
              type: ticketType,
              age: clickedData?.name,
              projectsFilter: repos,
              sprintId: selected.sprintId,
            }}
            selectedTeam={selectedTeam}
          />
        )}
    </>
  );
}
